import { PLAYABLE_MAP_COUNT } from '@kenocyber/shared';
import { SUPER_CANNONS_COUNT, SUPER_CANNONS_DURATION_MS } from '../cards/abilities';
import {
  MISSILE_HITBOX_BPS,
  MISSILE_LIFETIME_MS,
  MISSILE_SIZE_BPS,
  MISSILE_SPEED_BPS,
  MISSILE_TURN_DEGREES_PER_SECOND,
} from '../damage/missile';
import { SHIP_BASE_MAX_HEALTH } from '../hull/base-health';
import { MAP_DIFFICULTY_GROWTH_BPS_PER_MAP, mobScaleBpsForMap } from '../maps';

/**
 * Provisional balance for the first playable slice.
 *
 * Every tunable number of the hunt lives HERE and nowhere else. These values
 * make map 1 playable; they are NOT the final economy and must not be treated
 * as design decisions (see docs/OPEN_QUESTIONS.md for the pending formulas).
 *
 * The simulation runs in an abstract logical arena; one unit maps to one CSS
 * pixel at the reference resolution below.
 */
export const HUNT_BALANCE = Object.freeze({
  /** Logical arena the simulation reasons about. The renderer letterboxes it. */
  arena: Object.freeze({
    width: 1280,
    height: 720,
    /** The ship sits centred at this fraction of the arena height. */
    shipCenterYFraction: 0.9,
    /** Enemies are removed / leak once fully past this bottom edge. */
    bottomBoundary: 720,
  }),

  simulation: Object.freeze({
    /**
     * Fixed timestep, in whole milliseconds. All rules advance in ticks of
     * exactly this size.
     *
     * An INTEGER (16 ms, ~62.5 Hz) rather than `1000/60`: the accumulator is
     * integer arithmetic, which is what makes the outcome independent of how
     * wall-clock time is chunked into frames. This is the single source of
     * truth - `HUNT_TICK_MS` is derived from it.
     */
    tickMs: 16,
    /**
     * Largest wall-clock slice fed into the accumulator per frame. Caps
     * catch-up after a stall so no fake progress is granted. A backgrounded
     * tab is handled earlier and more strictly by `HuntClock`.
     */
    maxFrameDeltaMs: 250,
  }),

  ship: Object.freeze({
    /**
     * A vida-base, LIDA da fonte neutra em vez de escrita aqui.
     *
     * Continua provisória e continua a NÃO ser o valor de desenho pendente. O
     * que mudou é o dono: o duelo divide a geometria do casco por este mesmo
     * número, e enquanto ele viveu só aqui os dois modos podiam discordar sem
     * que nada os comparasse. Ver `hull/base-health.ts`.
     */
    baseHealth: SHIP_BASE_MAX_HEALTH,
    /** Base attack speed: 1 attack per second. */
    baseAttacksPerSecond: 1,
    /** Base damage of a single projectile before bonuses. */
    baseDamage: 1,
    /** Visual/logical size in arena units. */
    displayHeight: 96,
    displayWidth: 96,

    /**
     * How fast the nose swings toward the current target, radians per second.
     *
     * Fast enough to feel responsive, slow enough that the turn reads as a
     * movement rather than a teleport. Purely a presentation/aim value: it
     * never affects damage, attack speed or target selection.
     */
    facingTurnRadPerSecond: 10,
    /**
     * Angular difference below which the facing snaps to the target angle.
     *
     * Stops the nose from hunting back and forth by a fraction of a degree
     * when two enemies sit at almost the same position.
     */
    facingSnapRad: 0.004,
    /** Where the ship points with no target: straight up. */
    restingFacingRad: -Math.PI / 2,

    /**
     * How the ship wanders the arena.
     *
     * ## Why a sum of sines and not a random walk
     *
     * The server re-simulates every window and compares a digest, so anything
     * that decides where the ship is has to produce the SAME answer on both
     * sides. A random walk would be state: a position and a stream, in the
     * snapshot, versioned, restored, and wrong for every run written before it
     * existed. A sum of sines is a FUNCTION of the tick and the session seed -
     * both of which are already authoritative - so the position is derived,
     * never stored, and the two sides agree by construction with nothing added
     * to the snapshot at all.
     *
     * It still looks random. The four periods below are deliberately not
     * multiples of one another, so the combined path does not visibly repeat
     * inside any session anyone will play, and the phases are drawn from the
     * run's own seed so two runs wander differently.
     *
     * ## The vertical is one-sided
     *
     * Sideways the ship swings either way about the centre. Vertically it only
     * ever rises from its resting line, because below that line is the bottom
     * boundary where mobs leak - a ship that drifted down would sit in the
     * lane enemies are removed at, which reads as the ship falling out of the
     * arena even though nothing about the leak depends on where it is.
     */
    drift: Object.freeze({
      /**
       * Half the sideways swing, as a fraction of the arena width.
       *
       * ## Why 0.18, and not the 0.32 this started at
       *
       * The sideways offset is the only part of the wander that costs damage.
       * Every shot flies from a muzzle to an enemy somewhere above it, so a
       * ship under the lane has the shortest possible flight; one standing
       * four hundred units to the side has a longer one, and a turn-rate
       * limited shot spends part of that flight curving rather than closing.
       *
       * Measured at 0.32 it cost 2.6% of the damage over two minutes and 2% of
       * how long the ship survived at level one. Small - and small was enough
       * to matter once. The server's boss-handoff suite plays real windows
       * with a mid-game loadout, and there the ship started dying at 330
       * seconds against a boss countdown that needed 394, so the boss stopped
       * arriving at all. That was not a brittle test: it was a real threshold,
       * crossed.
       *
       * At 0.18 the ship still travels about four hundred and sixty units
       * across, several times its own width, which reads clearly as flying
       * around the map - and the suite passes with its usual margin. If this
       * is ever widened again, the boss countdown is what breaks first.
       */
      spanXFraction: 0.18,
      /** The whole upward swing, as a fraction of the arena height. */
      riseYFraction: 0.16,
      /**
       * Milliseconds per cycle: two waves across, two up. Coprime-ish on
       * purpose - shared factors would make the path close on itself.
       */
      periodsMs: Object.freeze([37_000, 23_500, 29_000, 17_500]),
      /** How the two waves of each axis share their amplitude. */
      blend: 0.62,
    }),

    /** Cannon sprite size, used to keep the formation clear of the hull. */
    cannonDisplaySize: 42,
    /**
     * Perpendicular gap between the projectiles emitted by ONE cannon.
     *
     * Small: a Perfect cannon's two shots leave side by side out of the same
     * muzzle, they do not fan across the whole wing.
     */
    projectileSpacing: 9,
  }),

  /**
   * Cannon mount geometry, in ship-local units (see `hunt/formation.ts`).
   *
   * The cannons are hardpoints along the hull, not a ring around it: one ahead
   * of the nose and a ladder of mirrored rungs stepping outwards and backwards
   * down the swept wing, so the battery follows the ship's own silhouette. Two
   * earlier shapes were rejected - a wing that pushed each pair 124 units off
   * the axis (the outer cannons read as escorts) and a circular arc of radius
   * 86 (evenly spaced, but still a ring floating around the hull).
   *
   * Every number here was measured against the master artwork at the configured
   * display sizes: the hull renders 92x88 units with a needle nose (half-width
   * 8 at 22 forward) opening to the wingtips (half-width 46 at 22 back), and a
   * cannon renders 22x40.
   */
  formation: Object.freeze({
    /**
     * The on-axis cannon that odd levels add, ahead of the nose.
     *
     * 76 leaves a 12-unit gap to the nose tip and stays clear of the innermost
     * rung, which sits 27 units off the axis - more than one cannon width.
     */
    centreDistance: 76,
    /**
     * Hardpoints down ONE wing, nose-first. Each becomes a mirrored pair, so
     * symmetry is structural rather than a coincidence of the numbers.
     *
     * `lateral` tracks the wing's own widening: the hull's half-width at that
     * slice, plus the cannon's half-width, plus daylight. The daylight is worth
     * a paragraph - a first pass sat 4-6 units off the art and read as welded
     * to the fuselage, so every rung was pushed out until the measured gap to
     * the silhouette is 11-19 units at every level. Wider than that and the
     * cannons start floating again, which is what the arc did.
     *
     * The 42-unit `forward` step is what keeps two 40-unit-long cannon sprites
     * from overlapping when they are stacked along the same wing edge.
     */
    cannonRungs: Object.freeze([
      Object.freeze({ forward: 58, lateral: 27 }),
      Object.freeze({ forward: 16, lateral: 50 }),
      Object.freeze({ forward: -26, lateral: 67 }),
    ]) as readonly { readonly forward: number; readonly lateral: number }[],
    /**
     * Which rungs each level occupies (odd levels also get the centre cannon,
     * so the counts come out 1..7). Levels do not simply add the next rung
     * outwards: they redistribute so the battery always looks balanced along
     * the wing instead of bunching at one end.
     */
    cannonLevelRungs: Object.freeze({
      1: Object.freeze([]),
      2: Object.freeze([0]),
      3: Object.freeze([1]),
      4: Object.freeze([0, 2]),
      5: Object.freeze([1, 2]),
      6: Object.freeze([0, 1, 2]),
      7: Object.freeze([0, 1, 2]),
    }) as Readonly<Record<number, readonly number[]>>,
    /**
     * Footprint of the cannon ARTWORK at `ship.cannonDisplaySize`, measured
     * from the master PNGs. The sprite box is square; the art inside is a
     * narrow barrel, which is why hardpoints can sit closer together laterally
     * than the sprite size suggests. Used to prove the mounts never overlap.
     */
    cannonArtWidth: 22,
    cannonArtLength: 40,
    /** How far ahead of a cannon body its muzzle sits. */
    muzzleLead: 22,
  }),

  allocation: Object.freeze({
    /** +1 base damage per point. Provisional. */
    damagePerPoint: 1,
    /** +10 base max health per point. Provisional. */
    healthPerPoint: 10,
    /** +0.02 base attacks per second per point. Provisional. */
    attackSpeedPerPoint: 0.02,
  }),

  mobs: Object.freeze({
    /** Map-1 mob health. Grows exponentially per map (see mobHealthForMap). */
    baseHealth: 3,
    /** Downward speed in units per second. */
    descentSpeed: 46,
    /** Base visual size (map 1) in arena units. Grows 10%/map visually. */
    displaySize: 84,
    /**
     * The stretch of time one spawn schedule covers, ms.
     *
     * Enemies arrive one at a time inside it rather than all at once: four
     * appearing together read as a single event on screen, however many
     * entities it really was.
     */
    spawnWindowMs: 2500,
    /**
     * Three spawn opportunities per window, each drawn from its own slice.
     *
     * Stratified rather than uniform on purpose. Three independent draws over
     * 2 500 ms regularly put two arrivals a few milliseconds apart, which is
     * the very clumping this replaced. One draw per region keeps the timing
     * unpredictable while guaranteeing the gaps: 400 ms is the closest two can
     * ever be, including across the seam between windows (2 300 -> 2 500 + 200).
     */
    spawnSlots: Object.freeze([
      Object.freeze({ fromMs: 200, toMs: 633 }),
      Object.freeze({ fromMs: 1033, toMs: 1466 }),
      Object.freeze({ fromMs: 1866, toMs: 2300 }),
    ]),
    /**
     * Max simultaneous normal mobs.
     *
     * A hard ceiling that protects the frame rate, never a queue: an
     * opportunity that arrives with a full field is dropped, not banked into a
     * burst later. The boss never counts against it.
     */
    maxAlive: 42,
    /** Minimum horizontal spacing between recent spawns, units. */
    minSpawnSpacing: 90,
    /** Attempts to find a non-overlapping spawn column. */
    spawnPlacementAttempts: 6,
    /**
     * XP per normal mob, as an exact fraction: 4 XP every 3 kills.
     *
     * Three arrivals a window at a flat 1 XP would have quietly slowed the
     * climb; the fraction keeps a full window worth the same 4 XP it has been
     * worth since the beginning. Accumulated, never rounded per mob:
     * `floor(kills x 4 / 3)` grows by 1 or 2 and lands on exactly 4 per 3.
     */
    xpRewardNumerator: 4,
    xpRewardDenominator: 3,
    /**
     * Gold per normal mob, as an exact fraction: 1 Gold every 2 kills.
     *
     * Gold is an integer in the ledger and must stay one, so the reward is
     * accumulated rather than rounded - `floor(kills / 2)` grows by 0 or 1 per
     * kill and lands on exactly 1 per 2. Three mobs a window average 1.5 Gold
     * every 2 500 ms against the 2 every 2 400 ms the original arena paid:
     * about 28% less per minute, holding the quarter-off intent.
     *
     * The counter behind it (`normalKills`) is authoritative snapshot state, so
     * a checkpoint splitting a pair, a reload, a spent point or a death never
     * loses or duplicates the fraction in flight.
     */
    goldRewardNumerator: 1,
    goldRewardDenominator: 2,
    /** Collision radius as a fraction of display size. */
    hitRadiusFraction: 0.38,
  }),

  projectiles: Object.freeze({
    /**
     * Travel speed in arena units per second.
     *
     * 30% quicker than the 520 it launched with, so a shot reaches its target
     * sooner. Nothing else about the shot moves with it: damage, cadence, how
     * many leave a volley, the dash artwork, its trail and its collision
     * radius are all unchanged.
     *
     * A second 30% was tried and taken back, and what it was tried FOR is
     * worth writing down.
     *
     * The goal was to space the shots out. On paper speed is a lever for that:
     * the gap between two consecutive shots is speed divided by cadence, so
     * +30% speed is +30% gap - and the stream also thins, because time of
     * flight falls by the same factor and the count in the air is cadence
     * times that time. In play it did not read as more spacing, and the reason
     * was not established before it was reverted.
     *
     * The other lever is cadence, and it is closed: attack speed here is
     * granted by items, and this project does not cap, divide or smooth an
     * item-granted attribute to make the visuals easier. So spacing is not
     * currently a number anyone can turn - it would need a change to how a
     * volley is emitted, not to how fast it travels.
     */
    speed: 676,
    /**
     * Max heading change while homing, radians per second.
     *
     * Raised by the same 30% as the speed. Homing curvature is angle per unit
     * TIME, so a faster shot with the old rate would sweep a wider arc and
     * start missing movers; scaling both together preserves the curve it draws
     * through space. It is not more aggressive homing - it is the same
     * geometry, travelled faster.
     */
    maxTurnRadPerSecond: 4.68,
    /** Collision radius in arena units. */
    hitRadius: 10,
    /** Removed once this far outside the arena. */
    despawnMargin: 80,
    /**
     * O Núcleo Atômico, na caça (gate V28G.J).
     *
     * As mesmas relações do duelo, derivadas das constantes da CAÇA em vez de
     * escritas outra vez. O casco da nave mede `displayHeight` unidades, e o §5
     * pede um diâmetro de explosão de cerca de três naves - portanto o raio é
     * uma nave e meia. Os outros dois seguem-se: proximidade é metade do raio da
     * explosão, e o corpo é o dobro de uma bala.
     *
     * Os números vivem aqui e não no renderer. A onda de choque que o jogador vê
     * é este raio, entregue pelo evento, e não uma aproximação artística dele.
     */
    bomb: Object.freeze({
      /** Velocidade, em basis points da de um tiro comum: 80 %. */
      speedBps: 8_000,
      /**
       * Quanto tempo uma bomba voa antes de detonar sozinha, em ms.
       *
       * Generoso de propósito: a arena tem 1 280 x 720 unidades e a bomba
       * atravessa-a bem dentro deste tempo, portanto o fim de vida é o último
       * recurso e não o caso normal. Existe porque o §3 exige que exista - uma
       * entidade autoritativa sem fim é uma entidade que uma sala acumula.
       */
      lifetimeMs: 4_000,
    }),
  }),

  /**
   * **As habilidades de carta** (gate V28H.H).
   *
   * Provisório e centralizado, como todo o resto desta tabela. O que NÃO está
   * aqui é o cooldown: ele é do catálogo (`cards.ts`), decidido por pessoas, e
   * lê-se de lá - copiá-lo para aqui criaria dois números a envelhecer.
   */
  cards: Object.freeze({
    superCannons: Object.freeze({
      /**
       * Seis segundos, em todos os níveis. Contrato aprovado.
       *
       * Em milissegundos inteiros porque o tique é 16 ms e o acumulador é
       * inteiro: `6_000` divide-se em 375 tiques exactos, sem resto a acumular.
       */
      durationMs: SUPER_CANNONS_DURATION_MS,
      /** Exactamente dois. Contrato aprovado, e o desenho depende disso. */
      count: SUPER_CANNONS_COUNT,
      /**
       * Onde os dois se montam, em unidades do casco.
       *
       * Atrás e ao lado da formação: `cannonRungs` chega a `lateral: 67` e
       * `forward: -26`, portanto 92 põe estes dois FORA da asa mais larga, e
       * -34 põe-nos atrás da fileira mais recuada. Nenhum dos dois se sobrepõe
       * ao casco (96 de largura, metade 48) nem a um canhão equipado.
       */
      lateral: 92,
      forward: -34,
      /**
       * Duas vezes a arte do canhão equipado.
       *
       * "Aproximadamente 2x" é o contrato, e dois exactos é a leitura mais
       * simples dele. A arte é a do canhão que a nave já usa - não há PNG novo.
       */
      artScale: 2,
    }),
  }),

  boss: Object.freeze({
    minDelaySeconds: 3 * 60,
    maxDelaySeconds: 16 * 60,
    /**
     * The scale the boss's HEALTH is computed at, for every tier alike.
     *
     * Size and difficulty used to be the same number: a random 2..13 decided
     * both, so a big boss was also a tough boss and the player learnt nothing
     * from the two being correlated. Now size comes from the encounter's tier
     * and health comes from here, so meeting a tier 6 is lucky rather than
     * punishing - which is the whole point of the tier table.
     *
     * Eight, because that is what the old roll actually produced. The arena is
     * 1280x720 with a 24-unit margin, so the fit limit was 672 and a map-1 mob
     * is 84 wide: every roll of 8 or above was clamped to 8, which is half of
     * them. Fixing it here keeps the boss as hard as the one being fought
     * today rather than quietly retuning the fight along with the sizes.
     */
    healthScale: 8,
    /**
     * Recovery after the boss kills you, in seconds of ACTIVE hunting.
     *
     * The boss used to come straight back the instant the player restarted,
     * which sounds fair and is a trap: with no escape rule a ship too weak to
     * win dies to it again immediately, earns nothing, and can never earn the
     * levels that would let it win. Three minutes of ordinary mobs is the
     * smallest thing that turns a permanent soft-lock into a setback - it pays
     * normal XP and Gold, so the attempt after it is stronger than the one
     * before, and it is the same encounter throughout: no new draw, no new
     * tier, no reward for coming back.
     *
     * Deliberately the same 180 s as the shortest scheduled delay. A recovery
     * that took longer than a fresh boss would make dying preferable to
     * winning.
     */
    retryWaitSeconds: 180,
    /** Margin kept between the boss sprite and the arena edges, units. */
    fitMargin: 24,
    /** Boss descends at this fraction of the normal mob speed. */
    descentSpeedFraction: 0.35,
    /** Gradual reveal duration (immobile, invulnerable), ms. */
    revealDurationMs: 3200,
    /**
     * Boss rewards, stated outright.
     *
     * They used to be multiples of the mob's reward, which meant retuning a
     * mob silently retuned the boss - and that is exactly what happened when
     * mob XP dropped from 4 to 1. The XP here is the same 240 the boss always
     * gave (the old 4 x 60); the Gold is 60, a deliberate 25% cut from 80, the
     * same reduction applied to normal mobs.
     */
    xpReward: 240,
    goldReward: 60,
  }),

  /** Rolling window for damage-per-minute and gold-per-minute. */
  metricsWindowMs: 60_000,
});

/**
 * Mob health per map: exponential growth from the map-1 base.
 *
 *   health(map) = baseHealth x growth^(map-1),   growth = 1.35 (provisional)
 *
 * Guarded to the playable range so a typo cannot silently overflow into
 * nonsense.
 *
 * ## DERIVADO da mesma constante que a recompensa (gate V28I.BP)
 *
 * Isto era o literal `1.35`, e a recompensa por morte era plana em todos os
 * mapas — 299,5x mais vida no mapa 20 pela mesma tarifa. Agora os dois lados
 * saem de `MAP_DIFFICULTY_GROWTH_BPS_PER_MAP`, que vive em `maps.ts` ao lado da
 * escala visual, e "a recompensa acompanha a vida 1:1" passa a ser uma
 * propriedade da estrutura em vez de dois números iguais escritos em ficheiros
 * diferentes.
 *
 * O valor não mudou: `(10000 + 3500) / 10000` é exactamente o mesmo `double`
 * que o literal `1.35`, porque a IEEE-754 arredonda correctamente o quociente
 * de dois inteiros exactamente representáveis. A vida de todos os mapas
 * continua bit a bit igual à de ontem.
 */
export const MOB_HEALTH_GROWTH_PER_MAP = (10_000 + MAP_DIFFICULTY_GROWTH_BPS_PER_MAP) / 10_000;

export function mobHealthForMap(mapIndex: number): number {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > PLAYABLE_MAP_COUNT) {
    throw new RangeError(`mapIndex must be an integer in [1, ${PLAYABLE_MAP_COUNT}]`);
  }
  const health = HUNT_BALANCE.mobs.baseHealth * MOB_HEALTH_GROWTH_PER_MAP ** (mapIndex - 1);
  if (!Number.isFinite(health)) {
    throw new RangeError(`mob health overflowed for map ${mapIndex}`);
  }
  return health;
}

/**
 * Mob visual size per map: +10% per map, compounding.
 *
 * ## One formula, consumed - not mirrored
 *
 * This used to compute `displaySize * 1.1 ** (mapIndex - 1)` of its own, and
 * the comment above it said it "mirrors `mobScaleBpsForMap`". A mirror is two
 * implementations of one rule, and two implementations of one rule are a
 * divergence waiting for somebody to fix a rounding bug in one of them. Worse,
 * these two disagreed in kind: the authoritative one accumulates in `bigint`
 * basis points precisely because 1,1 has no exact binary form, and this one
 * used the float it exists to avoid.
 *
 * It now reads the authoritative scale and converts once, here, at the
 * rendering edge - which is the only place a ratio is allowed to become a
 * `number`.
 */
export function mobDisplaySizeForMap(mapIndex: number): number {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > PLAYABLE_MAP_COUNT) {
    throw new RangeError(`mapIndex must be an integer in [1, ${PLAYABLE_MAP_COUNT}]`);
  }
  return (HUNT_BALANCE.mobs.displaySize * Number(mobScaleBpsForMap(mapIndex))) / 10_000;
}

/**
 * Boss health from mob health and the *effective rendered* scale:
 *
 *   bossHealth = mobHealth x ceil(12 x scale^1.35)
 *
 * Monotonic in scale; documented in docs/GAME_DESIGN.md. Provisional numbers,
 * centralised here.
 */
export function bossHealthMultiplier(effectiveScale: number): number {
  if (!Number.isFinite(effectiveScale) || effectiveScale <= 0) {
    throw new RangeError(`effectiveScale must be a positive finite number`);
  }
  return Math.ceil(12 * effectiveScale ** 1.35);
}

export function bossHealth(mobHealth: number, effectiveScale: number): number {
  return mobHealth * bossHealthMultiplier(effectiveScale);
}

/**
 * The XP curve lives in `hunt/progression.ts`.
 *
 * It is `100 + 10L² + L³` in exact `bigint` arithmetic. The float version that
 * used to live here (`ceil(10 · L^1.35)`) could not be exact, and a value that
 * has already passed through a float cannot be made exact again by casting it
 * to `bigint` afterwards.
 */

/**
 * Signed shortest angular distance from `from` to `to`, in `(-PI, PI]`.
 *
 * Turning by this value always takes the short way round: from 170 deg to
 * -170 deg it returns +20 deg, never -340 deg.
 */
export function shortestAngleDelta(from: number, to: number): number {
  const raw = (to - from) % (2 * Math.PI);
  if (raw > Math.PI) {
    return raw - 2 * Math.PI;
  }
  if (raw <= -Math.PI) {
    return raw + 2 * Math.PI;
  }
  return raw;
}

/** Wraps an angle into `(-PI, PI]` so the facing never drifts unbounded. */
export function normalizeAngle(angle: number): number {
  return shortestAngleDelta(0, angle);
}

/**
 * One step of the facing rotation.
 *
 * Rate-limited and applied per fixed simulation tick, so the result depends on
 * elapsed simulated time only - never on how many frames the browser drew.
 */
export function stepFacing(current: number, desired: number, dtSeconds: number): number {
  const delta = shortestAngleDelta(current, desired);
  if (Math.abs(delta) <= HUNT_BALANCE.ship.facingSnapRad) {
    return normalizeAngle(desired);
  }
  const maxStep = HUNT_BALANCE.ship.facingTurnRadPerSecond * dtSeconds;
  const step = Math.max(-maxStep, Math.min(maxStep, delta));
  return normalizeAngle(current + step);
}

/* -------------------------------------------------------------------------- */
/*  O Núcleo Atômico, em unidades da arena da caça (gate V28G.J)              */
/* -------------------------------------------------------------------------- */

/**
 * Os dois fatores da revisão visual, iguais aos do duelo.
 *
 * Escritos aqui em vez de importados de `pvp/balance.ts` porque a caça e o duelo
 * não partilham constantes - um raio da arena de um não significa nada no outro -
 * mas os FATORES são a mesma decisão do dono, e `atomic-bomb-hunt.test.ts`
 * verifica que os dois modos crescem o mesmo.
 */
export const HUNT_BOMB_BLAST_GROWTH = 4.5;
export const HUNT_BOMB_BODY_GROWTH = 2.9;

/**
 * **O raio da explosão: seis naves e três quartos.**
 *
 * O §5 pedia um diâmetro de "aproximadamente três naves de tamanho normal", e
 * uma nave da caça mede {@link HUNT_BALANCE}`.ship.displayHeight` unidades -
 * portanto três naves eram `3 x 96 = 288` de diâmetro, e o raio 144. Três
 * avaliações manuais depois, {@link HUNT_BOMB_BLAST_GROWTH} multiplica-o por
 * quatro e meio: **648**.
 *
 * DERIVADO do casco e não escrito, exatamente como o do duelo é derivado do
 * dele: é assim que o número continua a significar "três naves, vezes o fator"
 * quando a nave mudar de tamanho. **Não cresce com o nível nem com o número de
 * cópias.**
 *
 * E é dano, e não decoração: a onda desenhada é este raio, portanto uma explosão
 * maior fere na mesma medida mais longe.
 */
export const HUNT_BOMB_BLAST_RADIUS =
  ((HUNT_BALANCE.ship.displayHeight * 3) / 2) * HUNT_BOMB_BLAST_GROWTH;

/**
 * O CORPO da bomba: o dobro de uma bala, e depois quase o triplo disso.
 *
 * A medida de que a arte se serve para se dimensionar, e é ela que faz a bomba
 * ocupar mais mundo - não um tamanho fixo em pixéis. Ela continua a responder ao
 * zoom como tudo o resto da arena.
 *
 * A FORMA não mudou com o tamanho: a proporção vive na textura, e a segunda
 * revisão só a multiplicou. A bomba é a mesma, desenhada ao dobro.
 */
export const HUNT_BOMB_BODY_RADIUS = HUNT_BALANCE.projectiles.hitRadius * 2 * HUNT_BOMB_BODY_GROWTH;

/**
 * O raio de PROXIMIDADE: o corpo da bomba, mais 40 % de folga.
 *
 * A mesma ordem que o duelo mantém, e pela mesma razão:
 *
 *     corpo (58) < proximidade (81,2) < explosão (648)
 *
 * **Era metade da explosão, e deixou de ser** - a mesma correção que o duelo
 * levou. Uma explosão 2,5x maior arrastaria a caixa de disparo com ela, e a
 * bomba passaria a rebentar a duas naves de distância do mob. O tamanho da
 * explosão e a distância a que ela dispara são decisões diferentes.
 *
 * Presa ao CORPO, porque é o corpo que ela tem de conter: o círculo que envolve
 * a silhueta desenhada - aletas incluídas - é `1,13 x` o raio do corpo, e os
 * 40 % dão-lhe a margem por construção.
 */
export const HUNT_BOMB_PROXIMITY_RADIUS = HUNT_BOMB_BODY_RADIUS * 1.4;

/** A velocidade de uma bomba: 80 % da de um tiro comum, derivada. */
export const HUNT_BOMB_SPEED =
  (HUNT_BALANCE.projectiles.speed * HUNT_BALANCE.projectiles.bomb.speedBps) / 10_000;

/* -------------------------------------------------------------------------- */
/*  O Arsenal Ígneo, em unidades da arena da caça (gate V28G.L)               */
/* -------------------------------------------------------------------------- */

/**
 * A velocidade do míssil: **60,6 %** da de um tiro comum, derivada.
 *
 * `676 x 0,606 = 409,656` unidades por segundo - quase nove vezes a descida de
 * um mob (46 u/s) e vinte e cinco vezes a do boss, que são os alvos que ele
 * persegue. Derivada e não escrita, pela mesma
 * razão que a da bomba é: um número absoluto deixaria de significar "1,7x" no dia
 * em que o tiro mudasse de velocidade, e o duelo aplica o MESMO
 * {@link MISSILE_SPEED_BPS} à constante dele - é assim que "0,606x" quer dizer a
 * mesma coisa nos dois modos.
 */
export const HUNT_MISSILE_SPEED = (HUNT_BALANCE.projectiles.speed * MISSILE_SPEED_BPS) / 10_000;

/**
 * O limite de giro do míssil, em RADIANOS por segundo.
 *
 * 240 graus por segundo, convertidos aqui e uma vez só. A caça já faz
 * trigonometria - `Math.atan2` está no caminho de todos os tiros que perseguem -
 * portanto o limite vive na mesma unidade que `maxTurnRadPerSecond` já usa, e a
 * mesma decisão do contrato chega ao duelo como uma rotação inteira de passo
 * fixo. O NÚMERO é um só; as duas aritméticas é que são diferentes.
 *
 * Para escala: 4,19 rad/s contra os 4,68 de um tiro comum. O míssil vira um pouco
 * MENOS depressa do que uma bala, e é isso que faz a trajetória dele ler-se como
 * um arco em vez de uma linha que se dobra.
 */
export const HUNT_MISSILE_MAX_TURN_RAD_PER_SECOND =
  (MISSILE_TURN_DEGREES_PER_SECOND * Math.PI) / 180;

/**
 * O CORPO do míssil: **70 %** do corpo da bomba, em unidades da arena.
 *
 * `58 x 0,7 = 40,6`. É a medida de que a arte se serve para se dimensionar - o
 * comprimento desenhado é o dobro disto - e é o que faz o §"Tamanho" do contrato
 * ser verdade por construção nos dois modos: 70 % do comprimento visual e 70 %
 * da largura máxima saem os dois de uma escala UNIFORME sobre a mesma silhueta.
 *
 * Derivada da bomba e nunca copiada dela: "70 % da bomba" continua a ser verdade
 * quando a bomba mudar de tamanho, o que já aconteceu duas vezes.
 */
export const HUNT_MISSILE_BODY_RADIUS = (HUNT_BOMB_BODY_RADIUS * MISSILE_SIZE_BPS) / 10_000;

/**
 * A HITBOX do míssil: o corpo mais a margem declarada.
 *
 * `40,6 x 1,25 = 50,75`. Contém a silhueta desenhada inteira - aletas incluídas,
 * cujo círculo envolvente é `1,1334 x` o meio-comprimento - com cerca de 10 % de
 * folga, e é essa a "margem pequena e declarada" do contrato.
 *
 * **Não é o raio de proximidade da bomba.** A bomba detona ao APROXIMAR-SE
 * (`1,4 x` um corpo maior, 81,2 unidades); o míssil exige colisão física, e uma
 * caixa generosa fá-lo-ia rebentar ao largo do mob.
 */
export const HUNT_MISSILE_HIT_RADIUS = (HUNT_MISSILE_BODY_RADIUS * MISSILE_HITBOX_BPS) / 10_000;

/**
 * **O raio de curvatura mínimo do míssil, e a raiz das órbitas** (gate V28G.L.1).
 *
 * `v / w` — a velocidade a dividir pelo giro:
 *
 *     1 149,2 / 4,18879 = 274,4 unidades
 *
 * Numa arena de 1 280 por 720, um círculo de viragem de duzentas e setenta e
 * quatro é enorme: um inimigo que caia lá dentro não pode ser alcançado enquanto
 * o míssil virar sempre para o mesmo lado, por mais voltas que dê. Não é falta
 * de vontade, é falta de raio - e é por isso que a correção é sobre PARA QUE
 * LADO virar, e nunca sobre virar mais depressa.
 *
 * DERIVADO das duas constantes que o contrato congelou. Ninguém o escolhe.
 */
export const HUNT_MISSILE_TURN_RADIUS = HUNT_MISSILE_SPEED / HUNT_MISSILE_MAX_TURN_RAD_PER_SECOND;

/**
 * Para onde apontar: o ponto de INTERCEPTAÇÃO, e não onde o inimigo está.
 *
 * Duas passagens do ponto fixo, como o duelo faz: estimar o tempo pela distância
 * atual, mover o alvo, reestimar. Um mob desce a 46 unidades por segundo e o
 * míssil voa a 1 149 - a convergência é imediata, e a segunda passagem existe
 * para o boss, que é grande e lento mas não parado.
 *
 * O tempo é limitado ao que resta de vida, para um alvo inalcançável não fazer a
 * previsão divergir para fora do mundo.
 */
export function huntMissileAimPoint(
  from: { readonly x: number; readonly y: number },
  target: { readonly x: number; readonly y: number },
  velocity: { readonly x: number; readonly y: number },
): { readonly x: number; readonly y: number } {
  let aim: { readonly x: number; readonly y: number } = target;
  for (let pass = 0; pass < 2; pass += 1) {
    const distance = Math.hypot(aim.x - from.x, aim.y - from.y);
    const seconds = Math.min(MISSILE_LIFETIME_MS / 1000, distance / HUNT_MISSILE_SPEED);
    aim = { x: target.x + velocity.x * seconds, y: target.y + velocity.y * seconds };
  }
  return aim;
}

/**
 * Se o ponto de mira está DENTRO do círculo de viragem, ele é inalcançável.
 *
 * A mesma geometria do duelo: um míssil saturado no limite percorre uma
 * circunferência de raio {@link HUNT_MISSILE_TURN_RADIUS}, centrada
 * perpendicularmente à sua velocidade do lado para onde vira. A distância dele
 * ao alvo nunca desce abaixo de `R - |C - T|`, por mais voltas que dê.
 */
export function huntAimIsInsideTurn(
  missile: { readonly x: number; readonly y: number; readonly dirX: number; readonly dirY: number },
  aim: { readonly x: number; readonly y: number },
  captureRadius: number,
): boolean {
  const cross = missile.dirX * (aim.y - missile.y) - missile.dirY * (aim.x - missile.x);
  if (cross === 0) {
    return false;
  }
  const sign = cross > 0 ? 1 : -1;
  const centreX = missile.x - missile.dirY * sign * HUNT_MISSILE_TURN_RADIUS;
  const centreY = missile.y + missile.dirX * sign * HUNT_MISSILE_TURN_RADIUS;
  const reach = HUNT_MISSILE_TURN_RADIUS - captureRadius;
  if (reach <= 0) {
    return false;
  }
  return Math.hypot(aim.x - centreX, aim.y - centreY) < reach;
}

/**
 * **Que fração do passo o míssil percorre antes de tocar um círculo.**
 *
 * O teste que substitui a amostragem por pontos. Um míssil anda dezoito unidades
 * por tique, e um teste feito só no ponto de chegada começa a falhar assim que a
 * correção da órbita o põe a atacar de frente e à velocidade máxima. Um segmento
 * não tem buracos.
 *
 * `null` quando o segmento inteiro passa ao lado.
 */
export function huntSweepCircle(
  fromX: number,
  fromY: number,
  stepX: number,
  stepY: number,
  centre: { readonly x: number; readonly y: number },
  radius: number,
): number | null {
  const fx = fromX - centre.x;
  const fy = fromY - centre.y;
  const inside = fx * fx + fy * fy;
  if (inside <= radius * radius) {
    /* Já em contacto no início do passo. O toque é agora. */
    return 0;
  }
  const a = stepX * stepX + stepY * stepY;
  if (a === 0) {
    return null;
  }
  const b = fx * stepX + fy * stepY;
  const c = inside - radius * radius;
  const discriminant = b * b - a * c;
  if (discriminant < 0) {
    return null;
  }
  const at = (-b - Math.sqrt(discriminant)) / a;
  return at >= 0 && at <= 1 ? at : null;
}

/** Mob leak damage band (decided): 20%..100% of ship max health. */
export const LEAK_DAMAGE_MIN_FRACTION = 0.2;
export const LEAK_DAMAGE_MAX_FRACTION = 1;

/**
 * Damage taken when a mob fully crosses the bottom boundary:
 * proportional to the mob's remaining health, clamped to the decided band.
 */
export function mobLeakDamage(
  shipMaxHealth: number,
  mobRemainingHealth: number,
  mobMaxHealth: number,
): number {
  if (shipMaxHealth <= 0 || mobMaxHealth <= 0) {
    throw new RangeError('health values must be positive');
  }
  const proportional = shipMaxHealth * (mobRemainingHealth / mobMaxHealth);
  const minimum = shipMaxHealth * LEAK_DAMAGE_MIN_FRACTION;
  const maximum = shipMaxHealth * LEAK_DAMAGE_MAX_FRACTION;
  return Math.min(Math.max(proportional, minimum), maximum);
}

/**
 * **A espessura do feixe da Lança Solar na caça**, em bps do raio do casco.
 *
 * A gémea de `PVP_SOLAR_LANCE_WIDTH_BPS`, e com o mesmo valor: a carta tem a
 * mesma FORMA nos dois modos, e é isso que a proporção existe para garantir.
 * Ela vive de cada lado porque a unidade de cada arena é diferente - a mesma
 * divisão que os Super Canhões já tinham.
 *
 * Provisória, como todo o equilíbrio deste ficheiro.
 */
export const HUNT_SOLAR_LANCE_WIDTH_BPS = 6_000;

/**
 * **O alcance do feixe da Lança Solar na caça**, em bps do raio do casco.
 *
 * ## Porque o ALCANCE se dividiu em dois (gate V28H.P)
 *
 * Ele viveu em `cards/balance.ts`, uma proporção só para os dois modos - e o dono
 * viu o resultado: "a lança solar está muito pequena e de baixo alcance" no
 * duelo, depois de a aprovar na caça.
 *
 * A medição explica-o. A proporção é do CASCO, e os dois cascos não ocupam o
 * mesmo ecrã: a nave da caça mede 96 de 720 - **13 %** da altura da arena - e a
 * do duelo mede 52 000 de 15 000 000, que ao zoom por omissão dá **2 %** da
 * largura do ecrã. O mesmo múltiplo do casco produzia um feixe 3,7 vezes mais
 * curto e mais fino num modo do que no outro.
 *
 * Portanto o alcance passou a ser uma medida de ARENA, como a espessura já era,
 * e pela mesma razão exacta. Os dois números abaixo são escolhidos para o feixe
 * ocupar a MESMA fracção do ecrã nos dois modos - que é o que "a mesma carta"
 * quer dizer para quem olha.
 *
 * 250 000 é 25 raios - 1 200 unidades num casco de 96 - e a arena da caça tem
 * 1 280 de largura. O feixe atravessa quase toda ela, que é o que uma lança
 * solar carregada deve parecer.
 *
 * Provisório, como todo o equilíbrio deste ficheiro.
 */
export const HUNT_SOLAR_LANCE_REACH_BPS = 250_000;

/**
 * **A que velocidade a Esfera Ígnea viaja**, em unidades de mundo por segundo.
 *
 * Ela deixou de rebentar no instante da ordem (gate V28H.T): a descrição
 * aprovada fala de "uma grande esfera de fogo", e uma esfera é uma coisa que se
 * vê a caminho. Devagar de propósito - cerca de meio segundo a atravessar a
 * arena - porque o que se quer ver é a esfera, e não um lampejo.
 *
 * **De volta aos 900** (gate V28I.C). Ela subiu para 1 170 e depois 1 521 por
 * pedido do dono, mas os dois aumentos foram decididos a olhar para o DUELO -
 * onde a bola passou a ter setenta raios de casco e a arrastar-se lia-se como um
 * balão. Aqui a bola é pequena e a arena tem 1280 unidades: a velocidade
 * aprovada da caça é esta, e o gate que separou os dois modos devolveu-a.
 *
 * Provisória, como todo o equilíbrio deste ficheiro.
 */
export const HUNT_FIREBALL_SPEED = 900;

/**
 * **O raio da explosão da Esfera Ígnea na CAÇA**: seis raios de casco.
 *
 * O valor aprovado, e o que ele era antes de o duelo o levantar até setenta
 * (gate V28H.Z). Ele passou a viver aqui pela mesma razão que o da Lança já
 * vivia: as duas arenas têm escalas diferentes - um casco mede 96 unidades numa
 * caça de 1280 de largura, e 26 000 mu num duelo de 24 000 000 - e o mesmo
 * número dá bolas de tamanhos incomparáveis.
 *
 * O que a carga faz aqui é o que faz lá: gradua-o. O que muda é o tecto.
 */
export const HUNT_FIREBALL_REACH_BPS = 60_000;

/**
 * **Quanto tempo a Esfera Ígnea carrega na CAÇA: 400 ms, sozinha.**
 *
 * Decisão do dono (gate V28I.C), e o contrário do duelo: "um toque activa; carga
 * automática de 400 ms; não exige segurar nem soltar".
 *
 * Aqui a nave não é conduzida - ela voa sozinha e o jogador escolhe habilidades -
 * portanto uma carga de quinze segundos com a nave presa seria prender o jogador
 * ao ecrã a não fazer nada. Quatrocentos milésimos são o tempo de a bola nascer,
 * crescer e sair: uma antecipação que se vê, e que não se sofre.
 */
export const HUNT_FIREBALL_CHARGE_MS = 400;

/**
 * **O raio do CORPO da esfera na caça: 30% da nave, ou 60% de diâmetro.**
 *
 * Decisão do dono, escrita como ele a deu: "tamanho máximo aproximadamente 60%
 * da largura da nave da caça". Meio disso é o raio, e é daqui que saem as três
 * medidas que têm de coincidir - o corpo desenhado, o brilho à volta dele e a
 * distância a que ele apanha.
 *
 * **Não é uma fracção do raio da EXPLOSÃO**, e é a diferença que separa este
 * modo do duelo. Lá a bola cresce até quase preencher o próprio rebentamento,
 * porque quinze segundos de carga a pagam; aqui a explosão continua a valer os
 * seis raios de casco aprovados e o corpo é pequeno - uma bola de fogo do
 * tamanho da nave, e não um sol.
 */
export const HUNT_FIREBALL_BODY_BPS = 3_000;

/**
 * **O corpo do míssil do Catolé na CAÇA: um quinto do que era** (gate V28I.D).
 *
 * Decisão do dono a vê-lo voar: "o tamanho do míssil da bomba atómica é muito
 * desproporcional... diminua apenas a escala do míssil em 80% na caça". Ele
 * media 34% do raio da explosão - a fracção que a arte partilhada aplica quando
 * ninguém lhe dá um corpo - e 34% de uma explosão de seis vezes a Bomba é um
 * míssil maior do que a nave que o lançou.
 *
 * Duas reduções, as duas medidas a olhar para o ecrã: 34% menos oitenta por
 * cento deram 6,8%, e continuava grande; menos sessenta por cento disso são
 * **2,72%**. Para escala, é pouco mais de um décimo do raio do casco - um
 * míssil que se lê como um objecto lançado pela nave, e não como um segundo
 * casco a voar ao lado dela.
 *
 * **Só desenho.** Ao contrário do corpo da Esfera, este não decide colisão
 * nenhuma: o Catolé desta arena persegue o alvo e detona ao chegar a ele, e é o
 * tamanho do INIMIGO que fecha essa distância. Encolher o desenho não muda uma
 * única vida.
 *
 * **E não toca no duelo**, que era a outra metade da ordem: lá o corpo vem de
 * {@link PVP_CATOLE_BODY_RADIUS_MU}, três vezes o da Bomba Atômica, e continua
 * exactamente onde estava.
 */
export const HUNT_CATOLE_BODY_BPS = 272;

/**
 * E a do Catolé, mais lenta ainda.
 *
 * Ele é "uma bomba colossal teleguiada": massa lê-se como lentidão, e a dele é a
 * maior explosão do jogo. Um projéctil que chegasse depressa não daria tempo de
 * a antecipar.
 */
export const HUNT_CATOLE_SPEED = 620;
